import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMember } from "./MemberShell";
import { FileText, MapPin, Phone, Mail, FileSignature, Pencil, UserCog } from "lucide-react";
import { API } from "@/App";
import PhotoAvatar from "@/components/PhotoAvatar";
import DilekceDialog from "@/components/DilekceDialog";

const COMPANIES = [
  {
    key: "misas",
    label: "Misaş",
    title: "Mis-aş Gıda İnş. San. Ve Tic. A.Ş",
    cls: "company-misas",
    logo: "https://customer-assets-0z36b82j.emergentagent.net/job_partner-registry-2/artifacts/4mwtidfk_misas.png",
  },
  {
    key: "mis_holding",
    label: "Mis Holding",
    title: "Mis Holding A.Ş.",
    cls: "company-mh",
    logo: "https://customer-assets-0z36b82j.emergentagent.net/job_partner-registry-2/artifacts/4d24m9nu_misholding.webp",
  },
  {
    key: "koop",
    label: "Mis Koop.",
    title: "S.S. Mis Tüketim Kooperatifi",
    cls: "company-koop",
    logo: "https://customer-assets-0z36b82j.emergentagent.net/job_partner-registry-2/artifacts/azkn7y74_miskoop.png",
  },
];

async function downloadCert(company) {
  const token = localStorage.getItem("member_token");
  const r = await fetch(`${API}/member/certificate/${company}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: "include",
  });
  if (!r.ok) {
    const t = await r.json().catch(() => ({}));
    alert(t.detail || "Belge indirilemedi");
    return;
  }
  const blob = await r.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.target = "_blank"; a.rel = "noreferrer";
  a.download = `hisse-belgesi-${company}.pdf`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

export default function MemberPortal() {
  const { member, refresh } = useMember();
  const nav = useNavigate();
  const totalShares = (member.misas_hisse || 0) + (member.mis_holding_hisse || 0) + (member.koop_hisse || 0);
  const [dilekceOpen, setDilekceOpen] = useState(false);
  const [dilekceCompany, setDilekceCompany] = useState(null);

  // Only show the companies where this member actually holds shares.
  const myCompanies = COMPANIES.filter(c => Number(member[`${c.key}_hisse`] || 0) > 0);

  const openDilekce = (companyKey) => {
    setDilekceCompany(companyKey ? { key: companyKey } : null);
    setDilekceOpen(true);
  };

  const goProfile = (anchor) => {
    nav("/uye/profil" + (anchor ? `#${anchor}` : ""));
  };

  const EditPen = ({ testid, anchor, label = "Düzenle" }) => (
    <button type="button" onClick={() => goProfile(anchor)} data-testid={testid}
      title={label} aria-label={label}
      className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-500 hover:text-slate-900 border border-slate-200 hover:border-slate-400 rounded-full px-2 py-0.5 bg-white transition-colors">
      <Pencil size={10}/> {label}
    </button>
  );

  return (
    <div className="space-y-6 sm:space-y-8" translate="no">
      {/* Header with photo + greeting */}
      <div className="flex items-start gap-4 sm:gap-6 flex-wrap">
        <PhotoAvatar
          photoUrl={member.photo_url}
          name={member.ad_soyad}
          uploadUrl={`${API}/member/me/photo`}
          deleteUrl={`${API}/member/me/photo`}
          authHeader={{ Authorization: `Bearer ${localStorage.getItem("member_token") || ""}` }}
          onChange={() => refresh()}
          size={96}
        />
        <div className="flex-1 min-w-0">
          <div className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500 mb-2">Ortak Paneli</div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Merhaba, {member.ad_soyad.split(" ")[0]}</h1>
          <p className="text-slate-600 mt-2 text-sm">
            {myCompanies.length === 0
              ? "Hesabınıza tanımlı aktif hisse bulunmuyor."
              : myCompanies.length === 1
                ? <>Aşağıda <b>{myCompanies[0].title}</b> ortağı olarak hisse bilgileriniz yer alıyor.</>
                : <>Aşağıda ortağı olduğunuz <b>{myCompanies.length}</b> şirketteki hisse bilgileriniz yer alıyor.</>}
          </p>
        </div>
      </div>

      {/* Prominent "Update my info" CTA — always visible above the fold on mobile */}
      <button
        type="button"
        data-testid="portal-edit-profile-top"
        onClick={() => goProfile()}
        className="w-full flex items-center justify-between gap-3 rounded-xl bg-slate-900 hover:bg-slate-800 text-white px-4 sm:px-5 py-3.5 sm:py-4 shadow-md active:scale-[0.99] transition-transform"
      >
        <span className="flex items-center gap-3 min-w-0">
          <span className="h-9 w-9 rounded-full bg-white/10 flex items-center justify-center shrink-0">
            <UserCog size={18}/>
          </span>
          <span className="text-left min-w-0">
            <span className="block text-sm sm:text-base font-bold">Bilgilerimi Güncelle</span>
            <span className="block text-[11px] sm:text-xs text-white/70 truncate">Adres, telefon ve e-postanızı düzenleyin</span>
          </span>
        </span>
        <Pencil size={16} className="shrink-0 opacity-80"/>
      </button>

      {/* Company identity hero — logos + full titles of ONLY the member's companies */}
      {myCompanies.length > 0 && (
        <div data-testid="member-companies-hero"
             className={`grid gap-5 ${myCompanies.length === 1 ? "grid-cols-1" : myCompanies.length === 2 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1 md:grid-cols-3"}`}>
          {myCompanies.map((c, i) => (
            <div key={c.key} data-testid={`hero-${c.key}`}
                 className="crisp-card p-6 flex flex-col items-center text-center stagger-row"
                 style={{ animationDelay: `${i*80}ms` }}>
              <div className={`${myCompanies.length === 1 ? "h-24" : "h-20"} w-full flex items-center justify-center`}>
                <img src={c.logo} alt={c.title}
                     className="max-h-full max-w-[80%] object-contain" />
              </div>
              <div className={`mt-4 font-extrabold tracking-tight text-slate-900 ${myCompanies.length === 1 ? "text-2xl sm:text-3xl" : "text-lg"}`}>
                {c.title}
              </div>
              <div className="mt-2 text-xs uppercase tracking-[0.14em] text-slate-500 font-bold">Ortağı</div>
              <div className={`mt-3 inline-flex items-baseline gap-2 rounded-full px-4 py-1 ${c.cls}`}>
                <span className="font-mono-nums font-extrabold text-lg">{member[`${c.key}_hisse`]}</span>
                <span className="text-[11px] font-bold uppercase tracking-wider">hisse</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* KPI Row — only the member's companies */}
      <div className={`grid gap-6 ${myCompanies.length <= 1 ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"}`}>
        {myCompanies.map((c, i) => {
          const hisse = member[`${c.key}_hisse`] || 0;
          return (
            <div key={c.key} data-testid={`mkpi-${c.key}`} className="crisp-card p-6 stagger-row" style={{ animationDelay: `${i*60}ms` }}>
              <div className="flex items-start justify-between gap-2">
                <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider ${c.cls}`}>{c.label}</span>
                {hisse > 0 && (
                  <div className="flex gap-1.5">
                    <button
                      data-testid={`mcert-${c.key}`}
                      onClick={() => downloadCert(c.key)}
                      title="Hisse Pay Belgesi (PDF)"
                      className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-700 hover:text-slate-900 border border-slate-200 rounded-md px-2 py-1 bg-white hover:bg-slate-50">
                      <FileText size={12}/> Belge
                    </button>
                    <button
                      data-testid={`mdilekce-${c.key}`}
                      onClick={() => openDilekce(c.key)}
                      title="Hisse Satış / Ayrılış Dilekçesi Oluştur"
                      className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-white bg-slate-900 hover:bg-slate-800 rounded-md px-2 py-1">
                      <FileSignature size={12}/> Dilekçe
                    </button>
                  </div>
                )}
              </div>
              <div className="mt-6 flex items-baseline gap-2">
                <div className="text-5xl font-extrabold text-slate-900 font-mono-nums">{hisse}</div>
                <div className="text-slate-500 text-sm">hisse</div>
              </div>
              <div className="mt-2 text-xs text-slate-500">
                Kayıt No: <span className="font-mono-nums text-slate-800 font-semibold">{member[`${c.key}_kayit_no`] || "—"}</span>
                <span className="mx-2">·</span>
                Sayfa: <span className="font-mono-nums text-slate-800 font-semibold">{member[`${c.key}_sayfa_no`] || "—"}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Personal info */}
      <div className="crisp-card p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
          <h3 className="text-sm font-bold text-slate-900">Kişisel Bilgileriniz</h3>
          <button
            data-testid="portal-edit-profile"
            onClick={() => goProfile()}
            className="inline-flex items-center gap-2 rounded-md bg-slate-900 text-white text-xs font-bold px-3 py-2 hover:bg-slate-800"
          >
            <UserCog size={14}/> Bilgilerimi Güncelle
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500 mb-1.5">Ad Soyad</div>
            <div className="text-sm text-slate-900" data-testid="portal-adsoyad">{member.ad_soyad}</div>
          </div>
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500 mb-1.5">T.C. Kimlik</div>
            <div className="text-sm text-slate-900 font-mono-nums">{member.tc_kimlik || "—"}</div>
          </div>
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500 mb-1.5">Üyelik Tarihi</div>
            <div className="text-sm text-slate-900 font-mono-nums">{member.uyelik_tarihi || "—"}</div>
          </div>
          <div className="md:col-span-3">
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500 flex items-center gap-1.5"><MapPin size={12}/> Adres</div>
              <EditPen testid="edit-adres" anchor="adres" />
            </div>
            <div className="text-sm text-slate-900 whitespace-pre-wrap">{member.adres || <span className="text-slate-400">Eklenmemiş — düzenleyerek adresinizi girin</span>}</div>
          </div>
          <div className="md:col-span-3">
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500 flex items-center gap-1.5"><Mail size={12}/> E-posta</div>
              <EditPen testid="edit-email" anchor="email" />
            </div>
            <div className="text-sm text-slate-900">{member.email || <span className="text-slate-400">Eklenmemiş</span>}</div>
          </div>
          <div>
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500 flex items-center gap-1.5"><Phone size={12}/> Telefon 1</div>
              <EditPen testid="edit-tel1" anchor="tel1" />
            </div>
            <div className="text-sm text-slate-900 font-mono-nums">{member.tel1 || <span className="text-slate-400">—</span>}</div>
          </div>
          <div>
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500 flex items-center gap-1.5"><Phone size={12}/> Telefon 2</div>
              <EditPen testid="edit-tel2" anchor="tel2" />
            </div>
            <div className="text-sm text-slate-900 font-mono-nums">{member.tel2 || <span className="text-slate-400">—</span>}</div>
          </div>
          <div>
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500 flex items-center gap-1.5"><Phone size={12}/> GSM 1</div>
              <EditPen testid="edit-gsm1" anchor="gsm1" />
            </div>
            <div className="text-sm text-slate-900 font-mono-nums">{member.gsm1 || <span className="text-slate-400">—</span>}</div>
          </div>
          <div>
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500 flex items-center gap-1.5"><Phone size={12}/> GSM 2</div>
              <EditPen testid="edit-gsm2" anchor="gsm2" />
            </div>
            <div className="text-sm text-slate-900 font-mono-nums">{member.gsm2 || <span className="text-slate-400">—</span>}</div>
          </div>
        </div>
      </div>

      {totalShares === 0 && (
        <div className="crisp-card p-6 border-amber-200 bg-amber-50">
          <div className="text-sm text-amber-800">Hesabınıza tanımlı aktif hisse bulunmuyor. Bir hata olduğunu düşünüyorsanız yönetim ile iletişime geçin.</div>
        </div>
      )}

      {totalShares > 0 && (
        <div className="crisp-card p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h3 className="text-sm font-bold text-slate-900">Hisse Satış / Ayrılış Dilekçesi</h3>
              <p className="text-xs text-slate-600 mt-1 max-w-xl">
                Şirket tarafından belirlenen hisse fiyatı üzerinden hisselerinizi satmak istiyorsanız,
                Yönetim Kurulu Başkanlığına iletilmek üzere resmi dilekçenizi buradan oluşturabilirsiniz.
              </p>
            </div>
            <button
              data-testid="mdilekce-open"
              onClick={() => openDilekce(null)}
              className="inline-flex items-center gap-2 rounded-md bg-slate-900 text-white px-4 py-2 text-sm font-semibold hover:bg-slate-800">
              <FileSignature size={14}/> Dilekçe Oluştur
            </button>
          </div>
        </div>
      )}

      <DilekceDialog
        open={dilekceOpen}
        onClose={() => setDilekceOpen(false)}
        member={member}
        defaultCompany={dilekceCompany}
        onCreated={() => refresh()}
      />
    </div>
  );
}
